import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { ProductCardFooterItem } from "@/components/Layout/BaseLayouts/ProductCard/ProductCardFooterItem";
import { EsimQrCode } from "@/components/Portal/components/EsimQrCode/EsimQrCode.tsx";
import { DeviceProductCard } from "@/components/Portal/components/PortalDeviceTiles/TilesGrid/Tiles/DeviceProductCard/DeviceProductCard.tsx";
import { getIconForProductCategory } from "@/components/Portal/components/PortalDeviceTiles/TilesGrid/Tiles/DeviceProductCard/ProductCategoryIcon.tsx";
import { SimStatusChip } from "@/components/Portal/components/PortalDeviceTiles/TilesGrid/Tiles/DeviceProductCard/SimStatusChip.tsx";
import { ExtendedSimCardDetailWithEsimInfo } from "@/hooks/useExtendedSims.ts";
import { useGetTitle } from "@/hooks/useGetTitle.ts";
import { useIsMobile } from "@/hooks/useIsMobile";
import { portalRoutes } from "@/router/routes/portalRoutes";
import { getFlatNameFromDevice, isEsimQRInProgress } from "@/utils/deviceUtils";
import { tryNavigateToEsimInstall } from "@/utils/esimInstallUrl";

export const QRCodeTile = ({
  device,
}: {
  device: ExtendedSimCardDetailWithEsimInfo;
}) => {
  const { t } = useTranslation("portal");
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const title = useGetTitle(device.alias);
  const icon = getIconForProductCategory(device);
  const eSimInProgress = isEsimQRInProgress(device);
  const manageTarget = `${portalRoutes.manageDevice.base}?simId=${device.simId}`;
  const detailsTarget = `${portalRoutes.manageDevice.details.display}?simId=${device.simId}`;

  return (
    <DeviceProductCard
      dataTestid="qr-code-tile"
      icon={icon}
      title={title}
      subtitle={getFlatNameFromDevice(device)}
      chips={<SimStatusChip status={device.simStatus} />}
      navigationTarget={manageTarget}
      footer={
        eSimInProgress ? (
          <ProductCardFooterItem
            variant="notification"
            title={t("layout.tiles.qrCodeTile.inProcessText")}
            description={t("layout.tiles.qrCodeTile.qrPerEMail")}
          />
        ) : isMobile ? (
          // The QR code can't be scanned with the same device it's shown
          // on, so mobile gets a button to the Details tab instead.
          <ProductCardFooterItem
            variant="text-button"
            description={t("layout.tiles.qrCodeTile.activateTextMobile")}
            label={t("layout.tiles.qrCodeTile.installButton")}
            onClick={() => {
              if (!tryNavigateToEsimInstall(device.matchingId)) {
                navigate(detailsTarget);
              }
            }}
          />
        ) : (
          <ProductCardFooterItem
            variant="qr-code"
            qrCode={<EsimQrCode matchingId={device.matchingId} size={72} />}
            description={t("layout.tiles.qrCodeTile.activateText")}
            link={
              <a
                href={t("layout.tiles.qrCodeTile.instructionsLink")}
                target="_blank"
                rel="noreferrer"
                className="border-b border-foreground-primary py-2 text-body-xs font-semibold leading-none text-foreground-primary"
              >
                {t("layout.tiles.qrCodeTile.showInstructions")}
              </a>
            }
          />
        )
      }
    />
  );
};
